import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { PublicLayout } from "@/components/site/PublicLayout";
import { DiscordInviteCard } from "@/components/site/DiscordInviteCard";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Copy, ArrowLeft, ArrowRight, Check, Heart, Quote } from "lucide-react";

const formatDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }); } catch { return ""; }
};

export default function IssueDetailPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [sending, setSending] = useState(false);
  const [sentOk, setSentOk] = useState(false);
  const [sentTotal, setSentTotal] = useState(null);
  const [hp, setHp] = useState("");

  useEffect(() => {
    setLoading(true);
    api.get(`/public/issues/${slug}`)
      .then((r) => setData(r.data))
      .catch(() => setError("Issue not found"))
      .finally(() => setLoading(false));
    try {
      if (slug && localStorage.getItem(`cohnect_sent_${slug}`) === "1") setSentOk(true);
    } catch { /* ignore */ }
  }, [slug]);

  useEffect(() => {
    if (!data?.issue) return;
    const i = data.issue;
    document.title = `${i.title} \u00b7 Cohnect`;
    const seo = i.seo || {};
    const meta = document.querySelector('meta[name="description"]') || document.head.appendChild(Object.assign(document.createElement("meta"), { name: "description" }));
    meta.setAttribute("content", seo.meta_description || i.excerpt || "");
  }, [data]);

  if (loading) {
    return <PublicLayout><div className="mx-auto max-w-3xl p-8"><div className="h-48 nb-box animate-pulse" /></div></PublicLayout>;
  }
  if (error || !data?.issue) {
    return <PublicLayout>
      <div className="mx-auto max-w-3xl p-8">
        <h1 className="font-display text-3xl font-extrabold text-[color:var(--coh-ink)]">Not found</h1>
        <p className="mt-2 text-[color:var(--coh-ink-muted)]">This issue isn&apos;t available.</p>
        <Link to="/issues" className="mt-4 inline-block text-[color:var(--coh-blue)] underline underline-offset-4 decoration-2">Back to archive</Link>
      </div>
    </PublicLayout>;
  }

  const issue = data.issue;
  const prev = data.prev;
  const next = data.next;

  const copyMessage = async () => {
    try {
      await navigator.clipboard.writeText(issue.message_template || "");
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Could not copy");
    }
  };

  const iSentIt = async () => {
    if (sending || sentOk) return;
    setSending(true);
    try {
      const r = await api.post("/public/action-events", { issue_id: issue.slug, hp });
      setSentOk(true);
      setSentTotal(r.data?.total ?? null);
      try { localStorage.setItem(`cohnect_sent_${issue.slug}`, "1"); } catch { /* ignore */ }
      if (r.data?.recorded) {
        toast.success("Counted. Thank you.", { description: "You just bumped the human count by one." });
      } else {
        toast.success("Already counted for this issue.");
      }
    } catch (e) {
      const msg = e?.response?.status === 429 ? "Too many taps \u2014 give it a moment." : (e?.response?.data?.detail || "Please try again.");
      toast.error("Could not record", { description: msg });
    } finally {
      setSending(false);
    }
  };

  return (
    <PublicLayout>
      <article className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 pt-12 sm:pt-16 pb-16">
        <div className="flex items-center gap-3 text-xs text-[color:var(--coh-ink-muted)] font-mono" data-testid="issue-meta">
          <span className="text-[color:var(--coh-blue)]">Issue #{String(issue.issue_number).padStart(3, "0")}</span>
          <span>·</span>
          <time dateTime={issue.published_at}>{formatDate(issue.published_at || issue.updated_at)}</time>
          {issue.theme && (<><span>·</span><span className="uppercase tracking-[0.14em]">{issue.theme}</span></>)}
        </div>

        <h1 data-testid="issue-title" className="mt-5 font-display text-4xl sm:text-5xl font-extrabold tracking-[-0.03em] leading-[1.05] text-[color:var(--coh-ink)]">
          {issue.title}
        </h1>
        {issue.excerpt && <p className="mt-4 text-lg text-[color:var(--coh-ink-muted)] max-w-2xl leading-relaxed">{issue.excerpt}</p>}

        <div data-testid="issue-opening" className="prose-editorial mt-10">
          {(issue.opening_markdown || "").split(/\n\n+/).map((p, idx) => (
            <p key={idx}>{p}</p>
          ))}
        </div>

        {issue.inspiring_quote?.text && (
          <figure data-testid="issue-inspiring-quote" className="mt-10 pull-quote">
            <Quote aria-hidden="true" className="h-6 w-6 text-[color:var(--coh-blue)]" />
            <blockquote className="mt-3 font-display text-2xl sm:text-[1.75rem] font-bold leading-snug tracking-[-0.01em] text-[color:var(--coh-ink)]">
              “{issue.inspiring_quote.text}”
            </blockquote>
            {issue.inspiring_quote.author && (
              <figcaption className="mt-4 kicker" data-testid="issue-inspiring-quote-author">— {issue.inspiring_quote.author}</figcaption>
            )}
          </figure>
        )}

        {issue.reconnect_prompt && (
          <div data-testid="issue-reconnect-prompt-block" className="mt-10 p-6 sm:p-7 callout-blue">
            <div className="kicker">The Reconnect Prompt</div>
            <h2 className="mt-2 font-display text-2xl font-extrabold text-[color:var(--coh-ink)]">{issue.reconnect_prompt.headline}</h2>
            <p className="mt-3 text-[color:var(--coh-ink)]">{issue.reconnect_prompt.body}</p>
          </div>
        )}

        {issue.message_template && (
          <div data-testid="issue-what-to-send-block" className="mt-8 p-6 sm:p-7 callout-paper">
            <div className="flex items-center justify-between">
              <div className="kicker">What to send</div>
              <Button variant="outline" size="sm" onClick={copyMessage} data-testid="issue-what-to-send-copy-button" className="btn-nb btn-nb-secondary h-9 px-3">
                <Copy className="h-3.5 w-3.5 mr-2" /> Copy
              </Button>
            </div>
            <pre className="mt-4 whitespace-pre-wrap font-mono text-sm bg-[color:var(--coh-paper)] p-4 border-2 border-[color:var(--coh-ink)] text-[color:var(--coh-ink)]">{issue.message_template}</pre>
          </div>
        )}

        {issue.weekly_challenge && (
          <div data-testid="issue-weekly-challenge-block" className="mt-8 p-6 sm:p-7 callout-cyan">
            <div className="kicker">The Small Action</div>
            <h3 className="mt-2 font-display text-xl font-extrabold text-[color:var(--coh-ink)]">{issue.weekly_challenge.headline}</h3>
            <ol className="mt-4 space-y-2 list-decimal list-inside text-[color:var(--coh-ink)]">
              {(issue.weekly_challenge.steps || []).map((s, i) => <li key={i}>{s}</li>)}
            </ol>

            <div className="mt-6 pt-6 border-t-2 border-[color:var(--coh-ink)]">
              <input
                type="text"
                name="hp"
                value={hp}
                onChange={(e) => setHp(e.target.value)}
                aria-hidden="true"
                tabIndex={-1}
                autoComplete="off"
                style={{ position: "absolute", left: "-10000px", width: 1, height: 1, opacity: 0 }}
              />
              {sentOk ? (
                <div data-testid="i-sent-it-confirm" className="flex items-center gap-3 border-2 border-[color:var(--coh-ink)] bg-[color:var(--coh-paper-2)] px-5 py-3 w-fit shadow-nb">
                  <div className="h-8 w-8 border-2 border-[color:var(--coh-ink)] bg-[color:var(--coh-blue)] flex items-center justify-center text-[color:var(--coh-on-accent)]"><Heart className="h-4 w-4" /></div>
                  <div>
                    <div className="font-display text-sm font-extrabold text-[color:var(--coh-ink)]">Counted. Thank you.</div>
                    {sentTotal !== null && (
                      <div className="text-xs text-[color:var(--coh-ink-muted)]">The human count is now <span className="font-semibold text-[color:var(--coh-blue)]">{new Intl.NumberFormat().format(sentTotal)}</span>.</div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-4 flex-wrap">
                  <Button
                    onClick={iSentIt}
                    disabled={sending}
                    data-testid="i-sent-it-button"
                    className="btn-nb btn-nb-primary h-11 px-6"
                  >
                    <Check className="h-4 w-4 mr-2" /> {sending ? "Recording\u2026" : "I sent it"}
                  </Button>
                  <span className="text-xs text-[color:var(--coh-ink-muted)] max-w-sm">Tap once you&apos;ve sent your message. Anonymous — just adds one to the human count.</span>
                </div>
              )}
            </div>
          </div>
        )}

        {issue.closing_question && (
          <div data-testid="issue-closing-question" className="mt-10 p-6 sm:p-7 callout-purple">
            <div className="kicker">Closing question <span aria-hidden="true" className="font-mono">↓</span></div>
            <p className="mt-2 font-display text-xl sm:text-2xl font-bold text-[color:var(--coh-ink)]">{issue.closing_question}</p>
          </div>
        )}

        {/* Bottom Discord CTA (replaces the old email box) */}
        <div className="mt-12">
          <DiscordInviteCard variant="inline" testId="issue-discord-card" />
        </div>

        <div className="mt-12 grid gap-4 sm:grid-cols-2">
          {prev ? (
            <Link to={`/issues/${prev.slug}`} data-testid="issue-nav-prev" className="nb-box nb-interactive p-5">
              <div className="flex items-center gap-2 text-xs text-[color:var(--coh-ink-muted)] font-mono uppercase tracking-[0.14em]"><ArrowLeft className="h-3.5 w-3.5" /> Previous issue</div>
              <div className="mt-2 font-display text-lg font-extrabold text-[color:var(--coh-ink)]">{prev.title}</div>
            </Link>
          ) : <div />}
          {next ? (
            <Link to={`/issues/${next.slug}`} data-testid="issue-nav-next" className="nb-box nb-interactive p-5 sm:text-right">
              <div className="flex items-center gap-2 text-xs text-[color:var(--coh-ink-muted)] font-mono uppercase tracking-[0.14em] sm:justify-end">Next issue <ArrowRight className="h-3.5 w-3.5" /></div>
              <div className="mt-2 font-display text-lg font-extrabold text-[color:var(--coh-ink)]">{next.title}</div>
            </Link>
          ) : <div />}
        </div>
      </article>
    </PublicLayout>
  );
}
